/**
 * The Location editor's Timezone box — A9b, agreed by Rob 22/10/2026 (Q16 a).
 *
 * An office's timezone decides when ITS people get scheduled emails (the "who's off"
 * digests, birthday reminders, "still waiting" reminders) and when the company's
 * Teams/Slack channel gets that office's own "who's off" post (Q19 a).
 *
 * "Same as the company" ('' — stored as NULL) comes first and is the usual answer: a UK
 * company with three UK offices should not have to say London three times.
 *
 * ⚠️ THE SUGGESTION IS A SUGGESTION. When an office's country and the clock it follows
 * don't match (a US office on London time), the box offers the country's main timezone
 * as an amber line with a button. Nothing applies it — the same rule as the county →
 * bank-holiday region note: a suggestion the admin confirms is not a default they never
 * see. Pure functions, so `officeTimezone.checks.mjs` can pin them.
 */

export const SAME_AS_COMPANY = '';

/** "America/New_York" -> "New York". */
export const cityOf = (zone) => {
  if (!zone) return '';
  if (zone === 'UTC') return 'UTC';
  return zone.split('/').pop().replace(/_/g, ' ');
};

/** The box's two groups: the OFFICE's country first, then everywhere else. */
export const groupOptions = (timezones, officeCountry) => {
  const cc = (officeCountry || '').toUpperCase();
  const mine = [];
  const rest = [];
  (timezones || []).forEach((tz) => {
    if (cc && tz.country === cc) mine.push(tz);
    else rest.push(tz);
  });
  return { mine, rest };
};

/** Minutes a zone is ahead of UTC at `at` (negative behind). */
export const offsetMinutes = (zone, at = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at);
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  const atSeconds = Math.floor(at.getTime() / 1000) * 1000;
  return Math.round((asUtc - atSeconds) / 60000);
};

/** "08:00" on `fromZone`'s clock, read on `toZone`'s clock — 24-hour HH:MM (CONVENTIONS). */
export const sameMomentIn = (hhmm, fromZone, toZone, at = new Date()) => {
  const [h, m] = hhmm.split(':').map(Number);
  let mins = h * 60 + m - offsetMinutes(fromZone, at) + offsetMinutes(toZone, at);
  mins = ((mins % 1440) + 1440) % 1440;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
};

/**
 * The amber line, or null. `chosen` is the box's value ('' = same as the company);
 * `company` is {value, country} from GET /locations/timezone-options.
 *
 * Shown only when the clock the office FOLLOWS belongs to another country than the
 * office's own, and we know that country's main timezone. Never for UTC picked on
 * purpose — that is an answer, not an accident.
 */
export const suggestion = ({ officeCountry, chosen, company, timezones, mainZones, at }) => {
  const cc = (officeCountry || '').toUpperCase();
  if (!cc || chosen === 'UTC') return null;
  const effective = chosen || company?.value;
  if (!effective) return null;
  const effectiveCountry = chosen
    ? ((timezones || []).find((z) => z.value === chosen)?.country || null)
    : (company?.country || null);
  if (effectiveCountry === cc) return null;
  const main = (mainZones || {})[cc];
  if (!main || main === effective) return null;
  const others = (timezones || []).filter((z) => z.country === cc).length > 1;
  const arrives = sameMomentIn('08:00', effective, main, at);
  // ⚠️ NO COUNTRY NAME IN THE SENTENCE. The list's names carry no article, and "this office
  // is in United States" reads like a fault (seen on screen, 22/10/2026).
  const text = arrives === '08:00'
    ? `This office follows ${cityOf(effective)} time, which belongs to another country.`
    : `This office follows ${cityOf(effective)} time, so its 08:00 emails would arrive at ${arrives} ${cityOf(main)} time.`;
  return { zone: main, city: cityOf(main), text, others };
};
